import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Mail, ArrowLeft } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { useState } from 'react';
import { AddressBlock } from '../shared/AddressBlock';
import { StatusBadge } from '../shared/StatusBadge';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { POEmailModal } from './POEmailModal';
import { POPDFButton } from './POPDFButton';
import { ReceiveGoodsButton } from './ReceiveGoodsButton';
import { ROVASYS_PO_TERMS } from '../shared/poTermsTemplate';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Arial:wght@400;700&display=swap');

  .po-wrap {
    font-family: Arial, sans-serif;
    font-size: 12px;
    color: #000;
    background: #e8eaf0;
    min-height: 100vh;
    padding: 28px 20px 48px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .po-sheet {
    background: #fff;
    max-width: 960px;
    margin: 0 auto;
    padding: 20px 24px 24px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.12);
    border: 1px solid #ccc;
    position: relative;
  }

  /* ── HEADER BAR ── */
  .po-header-bar {
    display: grid;
    grid-template-columns: 200px 1fr 220px;
    align-items: center;
    border: 1.5px solid #000;
    margin-bottom: 0;
  }
  .po-header-logo {
    padding: 10px 12px;
    border-right: 1.5px solid #000;
  }
  .po-header-logo img { width: 160px; height: auto; display: block; }
  .po-header-title {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 10px;
  }
  .po-header-meta {
    border-left: 1.5px solid #000;
    padding: 8px 12px;
    font-size: 12px;
  }
  .po-meta-row {
    display: grid;
    grid-template-columns: 80px 10px 1fr;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 11px;
    align-items: center;
  }
  .po-meta-lbl { font-weight: 700; white-space: nowrap; text-transform: uppercase; color: #555; }
  .po-meta-val { font-weight: 700; color: #000; }

  /* ── FROM / TO / SHIP GRID ── */
  .from-to-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1.5px solid #000;
    border-top: none;
  }
  .ft-col { border-right: 1.5px solid #000; display: flex; flex-direction: column; }
  .ft-col:last-child { border-right: none; }
  .ft-label {
    font-weight: 700;
    font-size: 11px;
    background: #f0f0f0;
    padding: 4px 10px;
    border-bottom: 1px solid #ccc;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .ft-body {
    padding: 8px 12px;
    font-size: 11px;
    line-height: 1.6;
    flex: 1;
  }
  .ft-body strong { font-size: 12px; display: block; margin-bottom: 3px; }
  .ft-row { display: flex; gap: 4px; margin-bottom: 1px; }
  .ft-row-lbl { font-weight: 700; white-space: nowrap; min-width: 100px; }

  /* ── LOGISTICS ── */
  .logistics-bar {
    border: 1.5px solid #000;
    border-top: none;
  }
  .logistics-inner {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }
  .logistics-col {
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }
  .logistics-col:last-child { border-right: none; }
  .logistics-lbl {
    font-weight: 700;
    font-size: 11px;
    padding: 4px 10px;
    background: #f0f0f0;
    border-bottom: 1px solid #ccc;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .logistics-val {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }

  /* ── DEAR SIR ── */
  .dear-sir {
    padding: 8px 0 4px;
    font-size: 12px;
  }

  /* ── ITEMS TABLE ── */
  .items-tbl {
    width: 100%;
    border-collapse: collapse;
    border: 1.5px solid #000;
    font-size: 11px;
    margin-bottom: 0;
  }
  .items-tbl thead tr { background: #e8e8e8; }
  .items-tbl th {
    padding: 7px 6px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    border: 1px solid #000;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .items-tbl th.left { text-align: left; }
  .items-tbl td {
    padding: 8px 6px;
    border: 1px solid #000;
    text-align: center;
    vertical-align: middle;
  }
  .items-tbl td.left { text-align: left; }
  .items-tbl td.right { text-align: right; }
  .item-desc-main { font-weight: 700; font-size: 12px; }
  .item-desc-sub { font-size: 10px; color: #555; margin-top: 3px; white-space: pre-line; }

  /* ── TOTALS ── */
  .totals-section {
    border: 1.5px solid #000;
    border-top: none;
    display: grid;
    grid-template-columns: 1fr 280px;
  }
  .amount-words {
    padding: 8px 12px;
    font-size: 11px;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
  .amount-words b { font-size: 12px; }
  .totals-right { display: flex; flex-direction: column; }
  .t-row {
    display: flex;
    justify-content: space-between;
    padding: 5px 12px;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
  }
  .t-row:last-child { border-bottom: none; }
  .t-row-lbl { font-weight: 700; color: #333; font-size: 11px; }
  .grand-total-bar {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background: #1a1a1a;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
  }

  /* ── PAYMENT TERMS TABLE ── */
  .section-title {
    font-weight: 700;
    font-size: 12px;
    margin: 14px 0 6px;
    text-decoration: underline;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .pt-tbl {
    width: 100%;
    border-collapse: collapse;
    border: 1.5px solid #000;
    font-size: 11px;
    margin-bottom: 14px;
  }
  .pt-tbl thead tr { background: #e8e8e8; }
  .pt-tbl th {
    padding: 6px 8px;
    font-weight: 700;
    font-size: 11px;
    text-align: center;
    border: 1px solid #000;
    text-transform: uppercase;
  }
  .pt-tbl th.left { text-align: left; }
  .pt-tbl td {
    padding: 6px 8px;
    border: 1px solid #000;
    text-align: center;
    font-size: 11px;
  }
  .pt-tbl td.left { text-align: left; }
  .pt-tbl td.right { text-align: right; }

  /* ── INSTRUCTIONS / COMMENTS ── */
  .instructions-section {
    border: 1.5px solid #000;
    border-top: none;
  }
  .instr-row { display: flex; border-bottom: 1px solid #ccc; }
  .instr-row:last-child { border-bottom: none; }
  .instr-lbl {
    font-weight: 700;
    font-size: 11px;
    min-width: 140px;
    padding: 6px 12px;
    border-right: 1px solid #ccc;
    background: #f9f9f9;
    display: flex;
    align-items: flex-start;
  }
  .instr-val { padding: 6px 12px; font-size: 11px; line-height: 1.6; flex: 1; }

  /* ── T&C ── */
  .tc-section {
    border: 1.5px solid #000;
    border-top: none;
    padding: 10px 14px;
    font-size: 11px;
    line-height: 1.7;
  }
  .tc-title { font-weight: 700; font-size: 12px; margin-bottom: 6px; text-decoration: underline; }

  /* ── PO-5: STANDARD T&C ANNEXURE ── */
  .po-terms-annexure {
    border: 1.5px solid #000;
    padding: 12px 16px;
    font-size: 10px;
    line-height: 1.5;
    margin-top: 12px;
  }
  .po-terms-annexure-title {
    font-weight: 700;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: center;
    text-decoration: underline;
    margin-bottom: 10px;
  }
  .po-terms-section { margin-bottom: 7px; break-inside: avoid; page-break-inside: avoid; }
  .po-terms-section-title { font-weight: 700; font-size: 10.5px; margin-bottom: 2px; }
  .po-terms-para { margin: 0 0 2px; text-align: justify; }
  .po-terms-bullets { margin: 2px 0 2px 18px; padding: 0; }
  .po-terms-bullets li { margin-bottom: 1px; }

  /* ── SIGNATORY ── */
  .signatory-section {
    border: 1.5px solid #000;
    border-top: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .signatory-col {
    padding: 16px 14px;
    border-right: 1.5px solid #000;
    text-align: center;
    font-size: 11px;
  }
  .signatory-col:last-child { border-right: none; }
  .signatory-space { height: 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .signatory-lbl { font-weight: 700; font-size: 12px; }

  /* ── FOOTER ── */
  .po-footer-center {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid #000;
    border-top: none;
    padding: 7px;
    letter-spacing: 0.06em;
  }
  .po-footer {
    margin-top: 4px;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #666;
  }
  .status-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }

  /* ── PRINT STYLES ── */
  @media print {
    html, body {
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden;
    }
    .po-wrap, .po-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-wrap {
      position: absolute;
      left: 0;
      top: 0;
      margin: 0;
      padding: 0;
      background: white;
      width: 100%;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-sheet {
      display: flex !important;
      flex-direction: column !important;
      min-height: calc(297mm - 16mm) !important;
      box-shadow: none;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      max-width: 100%;
    }
    .print-border {
      display: block !important;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1.5px solid #000;
      pointer-events: none;
      z-index: 10000;
    }
    .print-spacer {
      display: block !important;
      flex-grow: 1 !important;
    }
    .status-row {
      display: none !important;
    }
    .items-tbl tr, .pt-tbl tr {
      page-break-inside: avoid;
    }
    .totals-section, .instructions-section, .signatory-section, .po-footer-center, .po-footer {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @page {
      margin: 8mm;
      size: A4;
    }
    .print-hide {
      display: none !important;
    }
    /* PO-5: T&C annexure starts on a fresh page when printed */
    .po-terms-annexure {
      page-break-before: always;
      border: none !important;
      padding: 0 !important;
      margin-top: 0 !important;
      font-size: 8.5px !important;
      line-height: 1.4 !important;
    }
    .po-terms-annexure-title { font-size: 12px !important; margin-bottom: 8px !important; }
    .po-terms-section-title { font-size: 9px !important; }
    .address-block {
      margin-bottom: 0 !important;
      padding: 5px 10px !important;
    }
    .address-block * {
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .items-tbl th, .items-tbl td {
      padding: 5px 6px !important;
      font-size: 10px !important;
      line-height: 1.35 !important;
    }
    .totals-section {
      grid-template-columns: 1fr 240px !important;
    }
    .totals-section .amount-words {
      padding: 6px 10px !important;
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .totals-section .t-row {
      padding: 4px 10px !important;
      font-size: 11px !important;
    }
    .grand-total-bar {
      padding: 6px 10px !important;
      font-size: 12px !important;
    }
    .signatory-col {
      padding: 6px 10px !important;
    }
    .signatory-space {
      height: 50px !important;
    }
    .stamp-img {
      height: 45px !important;
    }
    .tc-section {
      padding: 4px 10px !important;
      font-size: 7.5px !important;
      line-height: 1.25 !important;
    }
    .tc-title {
      margin-bottom: 2px !important;
      font-size: 9px !important;
    }
    .po-header-logo {
      padding: 6px 10px !important;
    }
    .po-header-logo img {
      width: 125px !important;
    }
    .po-header-title {
      font-size: 15px !important;
      padding: 6px !important;
    }
    .po-header-meta {
      padding: 6px 10px !important;
    }
    .po-meta-row {
      margin-bottom: 2px !important;
      font-size: 10px !important;
    }
    .dear-sir {
      padding: 4px 0 !important;
      font-size: 11px !important;
    }
    .section-title {
      margin: 8px 0 4px !important;
    }
    .pt-tbl th, .pt-tbl td {
      padding: 4px 6px !important;
      font-size: 10px !important;
    }
    .instructions-section .instr-lbl, .instructions-section .instr-val {
      padding: 3px 8px !important;
      font-size: 9.5px !important;
    }
  }
`;

const inr = (val: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

const amountInWords = (amount: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const numToWords = (n: number): string => {
        if (n === 0) return '';
        if (n < 20) return ones[n] + ' ';
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
        if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + numToWords(n % 100);
        if (n < 100000) return numToWords(Math.floor(n / 1000)) + 'Thousand ' + numToWords(n % 1000);
        if (n < 10000000) return numToWords(Math.floor(n / 100000)) + 'Lakh ' + numToWords(n % 100000);
        return numToWords(Math.floor(n / 10000000)) + 'Crore ' + numToWords(n % 10000000);
    };
    const wholePart = Math.floor(amount);
    const decimalPart = Math.round((amount - wholePart) * 100);
    let result = 'INR ' + numToWords(wholePart).trim() + ' Rupees';
    if (decimalPart > 0) result += ' and ' + numToWords(decimalPart).trim() + ' Paise';
    return result + ' Only';
};

const POShowActions = ({ onEmailClick, showWatermark, onToggleWatermark }: { onEmailClick: () => void, showWatermark: boolean, onToggleWatermark: () => void }) => (
    <div className="flex items-center gap-2">
        <Button
            variant={showWatermark ? "secondary" : "outline"}
            size="sm"
            onClick={onToggleWatermark}
            className={showWatermark ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
        >
            {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
            Watermark: {showWatermark ? "ON" : "OFF"}
        </Button>
        <ReceiveGoodsButton />
        <EditButton />
        <Button variant="outline" size="sm" onClick={onEmailClick}>
            <Mail className="w-4 h-4 mr-2" />
            Send Email
        </Button>
        <POPDFButton />
    </div>
);

const POShowContent = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext();
    const config = useConfigurationContext();
    if (!record) return null;

    return (
        <>
            <style>{css}</style>
            <div className="po-wrap">
                <div className="po-sheet">
                    <div className="print-border" />
                    <WatermarkOverlay isVisible={showWatermark} />

                    {/* ── HEADER BAR ── */}
                    <div className="po-header-bar">
                        <div className="po-header-logo">
                            <img src="/logos/rovasys_horizontal_clean.png" alt="Rovasys" />
                            <div style={{ fontSize: 9, color: '#555', marginTop: 4, lineHeight: 1.4 }}>
                                W-145, S-Block, MIDC-Bhosari, Pune - 411026<br />
                                Ph: +91 91722 01844 | info@rovasys.com
                            </div>
                        </div>
                        <div className="po-header-title">Purchase Order</div>
                        <div className="po-header-meta">
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">PO. NO</span>
                                <span>:</span>
                                <span className="po-meta-val">{record.reference}</span>
                            </div>
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Date</span>
                                <span>:</span>
                                <span className="po-meta-val"><DateField source="date" /></span>
                            </div>
                            {record.sales_order_id && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">SO. NO</span>
                                    <span>:</span>
                                    <span className="po-meta-val"><ReferenceField source="sales_order_id" reference="sales_orders" link="show"><TextField source="reference" /></ReferenceField></span>
                                </div>
                            )}
                            {record.vnd_code && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">VND CODE</span>
                                    <span>:</span>
                                    <span className="po-meta-val">{record.vnd_code}</span>
                                </div>
                            )}
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Status</span>
                                <span>:</span>
                                <span className="po-meta-val" style={{ textTransform: 'uppercase' }}>{record.status}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── PO-7: BILL TO (Rovasys) / SUPPLIER (vendor) / SHIP TO ── */}
                    <div className="from-to-grid border-l border-r border-black border-collapse">
                        <AddressBlock
                            label="Bill To"
                            addressOverride={
                                <>
                                    <strong>ROVASYS ALLIED WORKS PVT. LTD.</strong>
                                    W-145, S-Block, MIDC-Bhosari,<br />
                                    Pune, Maharashtra – 411026<br />
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>GSTIN:</span>
                                        <span>27AAMCR6049R1ZD</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>PAN:</span>
                                        <span>AAMCR6049R</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 80 }}>State Code:</span>
                                        <span>27</span>
                                    </div>
                                </>
                            }
                        />

                        <AddressBlock vendorId={record.vendor_id} label="Supplier">
                            {(record.vendor_contact_person || record.vendor_contact_no) && (
                                <div className="mt-1 pt-1 border-t border-dashed border-slate-200">
                                    {record.vendor_contact_person && (
                                        <div className="ft-row">
                                            <span className="ft-row-lbl">Kind Attn</span>
                                            <span>: {record.vendor_contact_person}</span>
                                        </div>
                                    )}
                                    {record.vendor_contact_no && (
                                        <div className="ft-row">
                                            <span className="ft-row-lbl">Contact No</span>
                                            <span>: {record.vendor_contact_no}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </AddressBlock>
                    </div>

                    <div className="ship-to-bar border-l border-r border-b border-black">
                        <AddressBlock 
                            companyId={record.company_id} 
                            useShipping 
                            label="Ship To"
                            addressOverride={
                                record.shipping_address ? (
                                    <div className="whitespace-pre-wrap">{record.shipping_address}</div>
                                ) : undefined
                            }
                        />
                    </div>

                    {/* ── LOGISTICS ── */}
                    <div className="logistics-bar">
                        <div className="logistics-inner" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                            {/* PO-10: Incoterms (stored in delivery_term via INCOTERM_CHOICES) */}
                            <div className="logistics-col">
                                <div className="logistics-lbl">Incoterms</div>
                                <div className="logistics-val">{record.delivery_term || 'EX-WORKS'}</div>
                            </div>
                            {/* PO-11: FOR / freight terms */}
                            <div className="logistics-col">
                                <div className="logistics-lbl">FOR (Freight)</div>
                                <div className="logistics-val">{record.freight_terms || '-'}</div>
                            </div>
                            <div className="logistics-col">
                                <div className="logistics-lbl">Payment Terms</div>
                                <div className="logistics-val">{record.payment_terms_text || 'IMMEDIATE'}</div>
                            </div>
                            <div className="logistics-col">
                                <div className="logistics-lbl">Delivery Date</div>
                                <div className="logistics-val"><DateField source="delivery_date" /></div>
                            </div>
                        </div>
                    </div>

                    {/* ── DEAR SIR ── */}
                    <div className="dear-sir">
                        Dear Sir,<br />
                        <span style={{ marginLeft: 20 }}>Please Supply the Following Material As Per Our Requirement</span>
                    </div>

                    {/* ── ITEMS TABLE ── */}
                    <table className="items-tbl">
                        <thead>
                            <tr>
                                <th style={{ width: 52, whiteSpace: 'nowrap' }}>S.No.</th>
                                <th className="left">Description</th>
                                <th style={{ width: 80 }}>HSN / SAC</th>
                                <th style={{ width: 56 }}>GST %</th>
                                <th style={{ width: 68 }}>QTY</th>
                                <th style={{ width: 52 }}>UOM</th>
                                <th style={{ width: 50 }}>%</th>
                                <th style={{ width: 100 }}>Rate</th>
                                <th style={{ width: 110 }}>Total Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {record.items?.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{String((index + 1) * 10).padStart(5, '0')}</td>
                                    <td className="left">
                                        <div className="item-desc-main">{item.description}</div>
                                        {item.item_terms && <div className="item-desc-sub">{item.item_terms}</div>}
                                    </td>
                                    <td>{item.hsn_code || '-'}</td>
                                    <td>{item.tax_rate ?? 18}%</td>
                                    <td style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN').format(item.quantity)}</td>
                                    <td>{item.unit || 'EA'}</td>
                                    <td>{item.percentage ? `${item.percentage}%` : '-'}</td>
                                    <td className="right">{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.unit_price)}</td>
                                    <td className="right" style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.line_total)}</td>
                                </tr>
                            ))}
                             <tr className="print-hide"><td colSpan={8} style={{ height: 40, borderBottom: 'none' }} /></tr>
                        </tbody>
                    </table>

                    {/* ── TOTALS ── */}
                    <div className="totals-section">
                        <div className="amount-words">
                            <b>Amount In Words:</b>
                            <span>{amountInWords(record.total_amount)}</span>
                        </div>
                        <div className="totals-right">
                            <div className="t-row">
                                <span className="t-row-lbl">Sub Total</span>
                                <span style={{ fontWeight: 700 }}>{inr(record.subtotal)}</span>
                            </div>
                            {record.cgst_amount > 0 && (
                                <div className="t-row">
                                    <span className="t-row-lbl">CGST 9.0%</span>
                                    <span style={{ fontWeight: 700 }}>{inr(record.cgst_amount)}</span>
                                </div>
                            )}
                            {record.sgst_amount > 0 && (
                                <div className="t-row">
                                    <span className="t-row-lbl">SGST 9.0%</span>
                                    <span style={{ fontWeight: 700 }}>{inr(record.sgst_amount)}</span>
                                </div>
                            )}
                            {(record.igst_amount > 0 || (!record.cgst_amount && !record.sgst_amount)) && (
                                <div className="t-row">
                                    <span className="t-row-lbl">IGST @ 18.00%</span>
                                    <span style={{ fontWeight: 700 }}>{inr(record.igst_amount || record.tax_amount)}</span>
                                </div>
                            )}
                            <div className="grand-total-bar">
                                <span style={{ letterSpacing: '0.08em' }}>GRAND TOTAL</span>
                                <span style={{ fontSize: 14 }}>{inr(record.total_amount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── REMARKS / INSTRUCTIONS ── */}
                    <div className="instructions-section">
                        {record.comments && (
                            <div className="instr-row">
                                <div className="instr-lbl">Remarks</div>
                                <div className="instr-val">{record.comments}</div>
                            </div>
                        )}
                        <div className="instr-row">
                            <div className="instr-lbl">Other Instructions</div>
                            <div className="instr-val" style={{ minHeight: 28 }}></div>
                        </div>
                    </div>

                    {/* ── PAYMENT TERMS TABLE ── */}
                    {record.payment_terms?.length > 0 && (
                        <>
                            <div className="section-title">Payment Terms</div>
                            <table className="pt-tbl">
                                <thead>
                                    <tr>
                                        <th style={{ width: 60 }}>Sr.No.</th>
                                        <th className="left">Payment Term</th>
                                        <th className="left">Description</th>
                                        <th>Invoice Portion</th>
                                        <th className="right" style={{ width: 140 }}>Payment Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {record.payment_terms.sort((a: any, b: any) => a.sr_no - b.sr_no).map((term: any, idx: number) => (
                                        <tr key={idx}>
                                            <td>{term.sr_no || idx + 1}</td>
                                            <td className="left">{term.payment_term}</td>
                                            <td className="left">{term.description}</td>
                                            <td>{term.invoice_portion}</td>
                                            <td className="right">{inr(term.payment_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    <div className="print-spacer" />

                    <div className="footer-group">
                        {/* ── TERMS & CONDITIONS ── */}
                        {record.terms_conditions && (
                            <div className="tc-section">
                                <div className="tc-title">Terms &amp; Conditions :</div>
                                <TextField source="terms_conditions" />
                            </div>
                        )}

                        {/* ── PO-9: STATUTORY / INVOICING NOTE ── */}
                        <div className="tc-section">
                            <div className="tc-title">Invoicing &amp; Statutory Instructions :</div>
                            <ol style={{ margin: 0, paddingLeft: 16 }}>
                                <li>Supply must be accompanied by a valid <strong>Tax Invoice</strong> and a valid <strong>E-Way Bill</strong> where applicable.</li>
                                <li>Your invoice must clearly carry: ACC No., GSTIN, Supplier Code, our PO No. ({record.reference}), Item No., Description, Quantity and HSN/SAC code for every line.</li>
                                <li>Goods/services without the above particulars are liable to be rejected and payment withheld.</li>
                                <li><strong>TDS</strong> will be deducted as per the Income Tax Act. In the absence of a valid PAN, TDS will be deducted at <strong>20%</strong>.</li>
                            </ol>
                        </div>

                        {/* ── SIGNATORY ── */}
                        <div className="signatory-section">
                            <div className="signatory-col">
                                <div className="signatory-space" />
                                <div className="signatory-lbl">Prepared By</div>
                            </div>
                            <div className="signatory-col">
                                <div className="signatory-space">
                                    <img
                                        src="/img/rovasys-stamp.png"
                                        alt="Company Stamp"
                                        className="stamp-img"
                                        style={{ height: 70, width: 'auto', objectFit: 'contain', opacity: 0.92 }}
                                    />
                                </div>
                                <div className="signatory-lbl">Authorised Signatory</div>
                                <div style={{ fontSize: 11, color: '#555' }}>Rovasys Allied Works Pvt Ltd</div>
                            </div>
                        </div>

                        <div className="po-footer-center">
                            System generated Document, Does not require any signature.
                        </div>
                        <div className="po-footer">
                            <span>Page: 1 / 1</span>
                            {config.showPrintTimestamp && (
                                <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                            )}
                        </div>
                    </div>

                    {/* ── PO-5: STANDARD TERMS & CONDITIONS ANNEXURE (prints on its own page) ── */}
                    <div className="po-terms-annexure">
                        <div className="po-terms-annexure-title">Purchase Order — Standard Terms &amp; Conditions</div>
                        {ROVASYS_PO_TERMS.map((section, i) => (
                            <div className="po-terms-section" key={i}>
                                <div className="po-terms-section-title">{section.title}</div>
                                {section.paras?.map((p, pi) => (
                                    <p className="po-terms-para" key={pi}>{p}</p>
                                ))}
                                {section.bullets && section.bullets.length > 0 && (
                                    <ul className="po-terms-bullets">
                                        {section.bullets.map((b, bi) => (
                                            <li key={bi}>{b}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

const POShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/purchase_orders" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Purchase Orders
                </Link>
            </Button>
            <span className="text-2xl font-bold tracking-tight">Purchase Order {record.reference}</span>
        </div>
    );
};

export const POShow = () => {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [showWatermark, setShowWatermark] = useState(true);

    return (
        <Show 
            title={<POShowTitle />}
            actions={<POShowActions onEmailClick={() => setEmailModalOpen(true)} showWatermark={showWatermark} onToggleWatermark={() => setShowWatermark(!showWatermark)} />}
        >
            <POShowContent showWatermark={showWatermark} />
            <POEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
        </Show>
    );
};
